import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { WideArrowDownward } from 'seagull/icons';
import { Checkbox, TextButton, Typography, DropdownMenu, useGlobalContext, ConfirmationModal, RadioButtonWithLabel } from 'seagull';
import { routes } from "../../constants";
import { selectors } from "../../store/state-selectors";
import { notificationSettingsModel } from "../../models";
import { useStore, useStoreActions } from "../../contexts";
import { handlePromise, numberAndUnit } from "../../infra/utils";
import { NotificationSettingsEntryType } from "../../models/notification-settings/model";
import { getVendorReminderDays, getClientReminderDays, getClientReminderTimes } from "../../business-logic/notificationSettings";
import { makeSingleNotificationSettingsEntryUpdate, makeRecurringNotificationSettingsEntryUpdate } from "../../models/notification-settings/makers";
import { Tab, Table, isDays, minutesToDays, daysToMinutes, getRecipientTypesText, handleSetRecipientTypes, getFallbackRecipientTypes, getEditDropdownRecipientTypeText } from "./NotificationSettings.utils";
import styles from "./EditNotificationSettingsModal.module.scss";
export const EditNotificationSettingsModal = _ref => {
  let {
    tableType,
    selectedTab,
    notification,
    notificationText,
    notificationName,
    refresh,
    dismissModal
  } = _ref;
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const businessId = useStore(selectors.user.businessId);
  const globalContext = useGlobalContext();
  const [times, setTimes] = useState(notificationSettingsModel.recurringTimes(notification));
  const [minutes, setMinutes] = useState(notificationSettingsModel.recurringMinutes(notification));
  const [recipientTypes, setRecipientTypes] = useState(notification.recipientTypes);
  const handleToggle = () => {
    setRecipientTypes(recipientTypes => getFallbackRecipientTypes({
      selectedTab,
      recipientTypes,
      originalRecipientTypes: notification.recipientTypes
    }));
  };
  return /*#__PURE__*/React.createElement(ConfirmationModal, {
    title: "Edit notification settings",
    "data-testid": "edit-notification-modal",
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.body
    }, /*#__PURE__*/React.createElement(Typography, {
      weight: "bold",
      "data-testid": "edit-notification-modal-notification-text"
    }, notificationText), /*#__PURE__*/React.createElement(EditModalOptions, {
      times: times,
      minutes: minutes,
      editKey: "".concat(selectedTab, "-").concat(tableType),
      recipientTypes: recipientTypes,
      notificationName: notificationName,
      recipientTypeOptions: notification.recipientTypeOptions,
      setMinutes: setMinutes,
      setTimes: setTimes,
      setRecipientTypes: setRecipientTypes,
      handleToggle: handleToggle
    })),
    primaryButtonLabel: "Save",
    primaryButtonTestid: "edit-notification-modal-save",
    onPrimaryButtonClick: async () => {
      if (!businessId) {
        history.push(routes.home);
        return;
      }
      const data = notification.type === NotificationSettingsEntryType.Recurring ? makeRecurringNotificationSettingsEntryUpdate(recipientTypes, {
        minutes: minutes || notification.recurring.minutes,
        times: times || notification.recurring.times
      }) : makeSingleNotificationSettingsEntryUpdate(recipientTypes);
      globalContext.startLoader('UpdateNotificationSettings');
      const result = await handlePromise(api.notification.updateNotificationSettings({
        data,
        businessId,
        notificationName
      }));
      if (result.success) {
        await refresh();
        globalContext.showSuccessToast('Notification settings updated successfully', 'edit-notification-success');
      } else {
        globalContext.showErrorToast('Failed to update notification settings', 'edit-notification-error');
      }
      globalContext.stopLoader('UpdateNotificationSettings');
      dismissModal();
    },
    secondaryButtonLabel: "Cancel",
    secondaryButtonTestid: "edit-notification-modal-cancel",
    onSecondaryButtonClick: dismissModal,
    onCloseButtonClick: dismissModal
  });
};
const EditModalOptions = _ref2 => {
  let {
    times,
    minutes,
    editKey,
    recipientTypes,
    notificationName,
    recipientTypeOptions,
    setTimes,
    setMinutes,
    setRecipientTypes,
    handleToggle
  } = _ref2;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    testId: "do-not-send-notification",
    label: /*#__PURE__*/React.createElement(Typography, {
      className: styles.option
    }, editDetailsMap[editKey].doNotSendText),
    checked: !notificationSettingsModel.hasRecipients({
      recipientTypes
    }),
    onChange: handleToggle
  }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    testId: "notify-recipients",
    label: /*#__PURE__*/React.createElement(Typography, {
      className: styles.option
    }, "".concat(editDetailsMap[editKey].sendText, " "), editDetailsMap[editKey].render({
      times,
      minutes,
      recipientTypes,
      recipientTypeOptions,
      notificationName,
      setTimes,
      setMinutes,
      setRecipientTypes
    })),
    checked: notificationSettingsModel.hasRecipients({
      recipientTypes
    }),
    onChange: handleToggle
  }));
};
const DropdownButton = _ref3 => {
  let {
    testid,
    dropdownItems,
    children
  } = _ref3;
  return children && /*#__PURE__*/React.createElement(DropdownMenu, {
    hAlign: "left",
    maxHeight: "50%",
    target: /*#__PURE__*/React.createElement(TextButton, {
      weight: "bold",
      suffixIcon: /*#__PURE__*/React.createElement(WideArrowDownward, null),
      "data-testid": testid,
      className: styles.textButton
    }, children)
  }, dropdownItems);
};
const editDetailsMap = {
  ["".concat(Tab.Vendor, "-").concat(Table.Notifications)]: {
    sendText: 'Notify',
    doNotSendText: 'Do not notify',
    render: _ref4 => {
      let {
        recipientTypes,
        notificationName,
        recipientTypeOptions,
        setRecipientTypes
      } = _ref4;
      return notificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/React.createElement(RecipientTypesDropdown, {
        recipientTypes: recipientTypes,
        notificationName: notificationName,
        recipientTypeOptions: recipientTypeOptions,
        setRecipientTypes: setRecipientTypes
      });
    }
  },
  ["".concat(Tab.Vendor, "-").concat(Table.Reminders)]: {
    sendText: 'Notify',
    doNotSendText: 'Do not notify',
    render: _ref5 => {
      let {
        minutes,
        recipientTypes,
        notificationName,
        recipientTypeOptions,
        setMinutes,
        setRecipientTypes
      } = _ref5;
      return !!minutes && notificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(RecipientTypesDropdown, {
        recipientTypes: recipientTypes,
        notificationName: notificationName,
        recipientTypeOptions: recipientTypeOptions,
        setRecipientTypes: setRecipientTypes
      }), "after ", /*#__PURE__*/React.createElement(DaysDropdown, {
        options: getVendorReminderDays(),
        minutes: minutes,
        setMinutes: setMinutes
      }));
    }
  },
  ["".concat(Tab.Client, "-").concat(Table.Notifications)]: {
    sendText: 'Notify my client',
    doNotSendText: 'Do not notify my client',
    render: () => /*#__PURE__*/React.createElement(React.Fragment, null)
  },
  ["".concat(Tab.Client, "-").concat(Table.Reminders)]: {
    sendText: 'Remind my client ',
    doNotSendText: 'Do not remind my client',
    render: _ref6 => {
      let {
        times,
        minutes,
        recipientTypes,
        setTimes,
        setMinutes
      } = _ref6;
      return !!minutes && !!times && notificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/React.createElement("span", null, "every ", /*#__PURE__*/React.createElement(DaysDropdown, {
        options: getClientReminderDays(),
        minutes: minutes,
        setMinutes: setMinutes
      }), "up to ", /*#__PURE__*/React.createElement(DropdownButton, {
        dropdownItems: getClientReminderTimes().map(timesOption => /*#__PURE__*/React.createElement(DropdownMenu.Item, {
          key: timesOption,
          onClick: () => setTimes(timesOption),
          "data-testid": "edit-times-dropdown-item-".concat(timesOption)
        }, /*#__PURE__*/React.createElement(Typography, null, numberAndUnit(timesOption, 'time')))),
        testid: "edit-times"
      }, numberAndUnit(times, 'time')));
    }
  }
};
const RecipientTypesDropdown = _ref7 => {
  let {
    recipientTypes,
    notificationName,
    recipientTypeOptions,
    setRecipientTypes
  } = _ref7;
  return /*#__PURE__*/React.createElement(DropdownButton, {
    dropdownItems: recipientTypeOptions.map(type => /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      key: type,
      as: Checkbox,
      closeOnClick: false,
      checked: recipientTypes.includes(type),
      onChange: e => handleSetRecipientTypes({
        newType: type,
        newRecipientTypes: e.currentTarget.checked ? [...recipientTypes, type] : recipientTypes.filter(x => x !== type),
        setRecipientTypes
      }),
      "data-testid": "edit-recipient-types-dropdown-item-".concat(type)
    }, /*#__PURE__*/React.createElement(Typography, null, getEditDropdownRecipientTypeText(notificationName)[type]))),
    testid: "edit-recipient-types"
  }, getRecipientTypesText(recipientTypes, notificationName).join(', '));
};
const DaysDropdown = _ref8 => {
  let {
    options,
    minutes,
    setMinutes
  } = _ref8;
  return /*#__PURE__*/React.createElement(DropdownButton, {
    dropdownItems: options.map(daysOption => /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      key: daysOption,
      onClick: () => setMinutes(daysToMinutes(daysOption)),
      "data-testid": "edit-days-dropdown-item-".concat(daysOption)
    }, /*#__PURE__*/React.createElement(Typography, null, numberAndUnit(daysOption, 'day')))),
    testid: "edit-days"
  }, isDays(minutes) ? numberAndUnit(minutesToDays(minutes), 'day') : numberAndUnit(minutes, 'minute'));
};